import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import getConfig from 'next/config';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/core/device';

import { useBrand } from '@atc/brand';
import { BrowserStorage } from '@atc/browser-storage';
import { eventBus } from '@atc/cai-event-bus';
import { useFeatures } from '@atc/features';
import { getFirstPartyVistorId, getPixallId } from '@atc/pixall-fns';
import { ClientOnly } from '@atc/react-client-only';
import { formatCurrency } from '@atc/string-fns';
import { calculateDownPayment } from '@atc/wallet-payment-fns';

import {
    LoadingSpinner,
    ModalScrollPanel,
    Panel,
    StickyContainer,
} from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import { emailFormEngagementClick, ownerEmailClick, ownerEmailSent, telMetricsPhoneTextClick } from 'reaxl-analytics-handlers';
import { sendEmailAndSaveDeal } from 'reaxl-email';
import { OwnerChatEvents } from 'reaxl-molecules';
import { AvailableOffers, DigitalRetailOverlayCTA, getAppliedDealerSettings, getIncentives, transformIncentives } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import { getDeal } from 'wallet-deals';
import {
    UDS_CONFIG_COMPLETED,
    UDS_CONFIG_REQUEST,
    useWalletCalcConfig,
    WALLET_EMAIL_DEALER_REQUEST,
} from 'wallet-manager';

import { getCompareListingsIds, getCompareListingsToggle } from '@/utilities/compareListingsHandler';
import configureExperianTile from '@/utilities/configureExperianTile';
import { getAbsolutePathWithListingId } from '@/utilities/vdpUrlBuilder';

import remoteActions from '@/actions/vdp/remoteActions';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import authDuck from '@/ducks/authDuck.js';
import birfDuck from '@/ducks/birfDuck.js';
import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import currentPageNameDuck from '@/ducks/currentPageNameDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import userDuck from '@/ducks/userDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpActiveEmailListingDuck from '@/ducks/vdp/vdpActiveEmailListingDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpAvailableOffersDuck from '@/ducks/vdp/vdpAvailableOffersDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import EmailOwnerFetcher from '@/fetchers/EmailOwnerFetcher';
import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';
import SmsSubscribeFetcher from '@/fetchers/SmsSubscribeFetcher';
import PersistDealDataService from '@/fetchers/vdp/PersistDealDataService';
import PsxPrequalEligibilityCheckFetcher from '@/fetchers/vdp/PsxPrequalEligibilityCheckFetcher';

import useLeadAdditionalInventory from '@/hooks/useLeadAdditionalInventory';
import usePreQualEligibility from '@/hooks/usePreQualEligibility';

import EmailFormNextStepsContainer from '@/containers/EmailFormNextStepsContainer';
import WalletAlertsContainer from '@/containers/WalletAlertsContainer';

import Anchor from '@/components/Anchor/index.js';
import LazyComponent from '@/components/LazyComponent';
import AffixedSidebar from '@/components/vehicledetails/AffixedSidebar';
import OfferSummary from '@/components/vehicledetails/OfferSummary';

import LeaderBoardAdSlotContainer from './LeaderBoardAdSlotContainer';
import LenderPreQualWrapper from './LenderPreQualWrapper';
import ListingErrorContainer from './ListingErrorContainer';
import MediaGallery from './MediaGallery';
import PricingSnapshotContainer from './PricingSnapshotContainer';
import PrimaryVdpCallsToActionContainer from './PrimaryVdpCallsToActionContainer';
import PurchaseWithConfidenceSnapshotContainer from './PurchaseWithConfidenceSnapshotContainer';
import ShowcaseAdSlotContainer from './ShowcaseAdSlotContainer';
import SpecificationsGridContainer from './SpecificationsGridContainer';
import StandaloneCreditAppContainer from './StandaloneCreditAppContainer';
import VdpPaginationContainer from './VdpPaginationContainer';
import VehicleDetailsEmailModalContainer from './VehicleDetailsEmailModalContainer';
import VehicleDetailsTitleContainer from './VehicleDetailsTitleContainer';

const LowerDetails = dynamic(() => import('@/containers/vdp/sections/LowerDetails'), { ssr: false });

const AdditionalVehicleDetailsContainer = dynamic(() => import('@/containers/vdp/AdditionalVehicleDetailsContainer'), { ssr: false });
const BatteryHealthContainer = dynamic(() => import('@/containers/vdp/BatteryHealthContainer'), { ssr: false });
const CertifiedProgramOfferContainer = dynamic(() => import('@/containers/vdp/CertifiedProgramOfferContainer'), { ssr: false });
const MyWalletContainer = dynamic(() => import('@/containers/vdp/MyWalletContainer'), {
    ssr: false,
    loading: () => (
        <LoadingSpinner
            id="nativeDealContainer"
            height={400}
        />
    ),
});
const PreQualCTAContainer = dynamic(() => import('@/containers/vdp/PreQualCTAContainer'), { ssr: false });
const PricingDetailsContainer = dynamic(() => import('@/containers/vdp/PricingDetailsContainer'), { ssr: false });
const PurchaseWithConfidenceContainer = dynamic(() => import('@/containers/vdp/PurchaseWithConfidenceContainer'), { ssr: false });
const RatingsAndReviewsContainer = dynamic(() => import('@/containers/vdp//RatingsAndReviews/RatingsAndReviewsContainer'), { ssr: false });
const StickyHeaderContainer = dynamic(() => import('@/containers/vdp/StickyHeaderContainer'), { ssr: false });
const VehicleFeaturesContainer = dynamic(() => import('@/containers/vdp/VehicleFeaturesContainer'), { ssr: false });

// Default
const DealerLeadsContainer = dynamic(() => import('./DealerLeadsContainer'), { ssr: false });

// PSX
const PsxBubbleChat = dynamic(() => import('@/containers/vdp/psx/PsxBubbleChat'), { ssr: false });
const PsxCtaContainer = dynamic(() => import('@/containers/vdp/psx/PsxCtaContainer'), {
    ssr: false,
    loading: () => (
        <LoadingSpinner
            className="margin-top-4 margin-bottom-5"
            height={40}
        />
    ),
});
const PsxLeadsContainer = dynamic(() => import('@/containers/vdp/psx/PsxLeadsContainer'), { ssr: false });
const PsxPrimerContainer = dynamic(() => import('@/containers/vdp/psx/PsxPrimerContainer'), { ssr: false });
const PsxVerificationContainer = dynamic(() => import('@/containers/vdp/psx/PsxVerificationContainer'), { ssr: false });

const similarIventoryFallback = (
    <div
        id="placeholder-similar-inv"
        style={{ height: 370 }}
    />
);
const SimilarInventoryContainer = dynamic(() => import('./SimilarInventoryContainer'), {
    ssr: true,
    loading: () => similarIventoryFallback,
});

/*
 * !xs:
 * `-----------------------------`
 * |    Sticky Header (<lg)      |
 * |.............................|
 * |         Navigation          |
 * |.............................|
 * | Banner                      |
 * |.............................|
 * |                  |          |
 * | Media            | Sidebar  |
 * | Upper Details    |          |
 * |.............................|
 * | Lower Details               |
 * |-----------------------------|
 * | Support Containers (hidden) |
 *  ----------------------------
 *
 * xs:
 * `------------------`
 * |  Sticky Header   |
 * |..................|
 * |  Navigation      |
 * |..................|
 * |  Banner          |
 * |..................|
 * |  Media           |
 * |..................|
 * |  Sidebar         |
 * |..................|
 * |  Upper Details   |
 * |..................|
 * |        AD        |
 * |..................|
 * |  Lower Details   |
 * |------------------|
 * | Support (hidden) |
 *  ------------------
 *
 * Default Banner Contents
 * 1. Listing Error
 * 2. Title Container
 *
 * Default Media Contents
 * 1. Media Gallery
 * 2. Specifications
 *
 * Default Sidebar Contents
 * 1. Pricing Snapshot
 * 2. Prequal
 * 3. Esntial
 * 4. Owner Details Snapshot
 * 5. Email Form
 * 6. Ad
 *
 * Default Upper Details Contents
 * 1. Wallet / NDS
 * 2. Seller Comments
 * 3. Vehicle Features
 * 4. Price Details
 * 5. Certified Pre-Owned
 * 6. Purchase With Confidence
 * 7. Ads
 * 8. Rating and Reviews
 *
 * Default Lower Details Contents
 * 1. Owner Details
 * 2. Similar Vehicles
 * 3. At Home Services
 * 4. Additional Resources
 * 5. Fraud Report
 *
 * Default Support Containers
 * 1. Email Modal
 * 2. Third Party Chat Events
 * 3. Stand Alone Credit App
 *
*/

// Should probably clean this up so we don't have to add to this every time
const thirdPartyCertTypes = ['FORD_BLUE_CERT', 'FORD_EV_CERT', 'FORD_CERT', 'NISSAN_SELECT_CERT', 'INFINITI_SELECT_CERT', 'CARBRAVO_OFF_CERT', 'CARBRAVO_OFF_BUDGET_CERT', 'HONDA_OEM_USED', 'ACURA_OEM_USED', 'STELLANTIS_OFF_CERT'];

const AFFIX_ID = 'affixedBottomBoundary';

export function VehicleDetailsContainer() {
    // Feature Flags
    const {
        accelerate: [, {
            credit_app: enableSDPCreditApp,
        }],
        ads: [isAdsEnabled, { mma: isMmaEnabled, mrec: isMrecEnabled }],
        battery_health: [enableBatteryHealth, {
            images: batteryHealthImages,
            health_rating_threshold: healthRatingThreshold,
            ford_battery_health: fordBatteryHealth,
        }],
        brand: [, {
            consumer_brand: consumerBrand,
            leads_cc_flag: copySelf,
            leads_partner_name: partnerName,
        }],
        chat: [isChatEnabled, { providers: chatProviders }],
        consumer_ratings: [isConsumerRatingsEnabled, { vdp_ratings: isConsumerRatingsVdpEnabled }],
        esntial_confident_purchase: [isEsntialConfidentPurchase],
        my_wallet: [, { enable_deals: enableDeals }],
        nds_calc: [isNDSCalcEnabled],
        nds_right_rail: [enableNDSRightRail],
        one_click_lead_vdp: [enableOneClickVDP],
        remove_carfax_tile: [removeCarfaxTile],
        similar_inventory: [isSimilarInventoryEnabled],
        standalone_credit_app: [isStandaloneCreditAppEnabled],
        show_wallet_alert_notification: [showWalletAlertNotification],
        vdp_carousel_higher_placement: [isVdpCarouselHigherPlacementEnabled],
    } = useFeatures([
        'accelerate',
        'ads',
        'battery_health',
        'brand',
        'chat',
        'consumer_ratings',
        'esntial_confident_purchase',
        'my_wallet',
        'nds_calc',
        'nds_right_rail',
        'one_click_lead_vdp',
        'remove_carfax_tile',
        'similar_inventory',
        'standalone_credit_app',
        'show_wallet_alert_notification',
        'vdp_carousel_higher_placement',
    ]);

    const device = useDevice();
    const dispatch = useDispatch();
    const isXs = _get(device, 'is.xs', false);
    const isGreaterThanSm = _get(device, 'greaterThan.sm', false);
    const isLg = _get(device, 'is.lg', false);

    const { brand, brands, isBrand } = useBrand();
    const isAtcBranded = isBrand(brands.ATC_BRAND);
    const isKbbBranded = isBrand(brands.KBB_BRAND);

    const [cookies = {}] = useCookies(['ATC_ID', 'pxa_ipv4']);

    const { publicRuntimeConfig } = getConfig();
    const { atcHost, environment } = publicRuntimeConfig;

    // Get data from Redux
    const creditAppHost = useSelector(globalConfigsDuck.selectors.getCreditAppHost);
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const hasVehicleReviews = useSelector(vdpResultsDuck.selectors.hasVehicleReviews);
    const birf = useSelector(birfDuck.selectors.getPageBirf);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const memoizedQuery = useMemo(() => query || {}, [query]);
    const showEmailModal = useSelector(vdpActiveInteractionDuck.selectors.getShowEmailModal);
    const showEmailConfirmationModal = useSelector(vdpActiveInteractionDuck.selectors.getShowEmailConfirmationModal);
    const { availableOffers } = useSelector(vdpAvailableOffersDuck.selectors.getDuckState);
    const deal = useSelector(vdpResultsDuck.selectors.getDealInfo, _isEqual);
    const listingIncentives = useSelector(paymentsDuck.selectors.getListingIncentives) || {};
    const pencilingPayments = useSelector(paymentsDuck.selectors.getPencilingPayments) || {};
    const listingPayments = useSelector(paymentsDuck.selectors.getListingPayments) || {};
    const { incentiveIds } = useSelector((state) => paymentsDuck.selectors.getLowestPencilingPaymentByListingId(state, listing?.id));
    const existingDeal = useSelector((state) => dealsDuck.selectors.getDealByListingVin(state, listing?.vin));
    const memoizedExistingDeal = useMemo(() => existingDeal || {}, [existingDeal]);
    const payments = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    const interestRates = useSelector((state) => state?.interestRates) || fallbackInterestRates;
    const leadProfile = useSelector(userDuck.selectors.getLeadProfile);
    const { dealType, interactions = {} } = payments;
    const { paymentEmphasis, hasUpdatedWallet, hasUpdatedNDSCalc } = interactions;
    const isOneClickVDPEnabled = enableOneClickVDP && leadProfile?.isOneClickOptedIn;
    const isEsntialConfidentPurchaseEnabled = isEsntialConfidentPurchase && listing?.esntial && !listing?.esntial?.deemphasized;
    const { cashDealPrice, disableTrade } = useWalletCalcConfig(payments, listing);
    const dealXgIdRef = useRef();
    dealXgIdRef.current = memoizedExistingDeal?.id;

    // Used for handling email input field interaction analytics tagging
    const emailInputClickedRef = useRef(null);

    /**
     * Initialize the Wallet Configuration Manager
     */
    const [config, setConfig] = useState({});
    const { disablePriceBreakdown, paymentIncentivesFetcher, walletZip, shopperZip, dealerZip } = config;

    const zip = useSelector(userDuck.selectors.getZip) ?? memoizedQuery?.zip ?? walletZip ?? shopperZip ?? dealerZip ?? listing?.owner?.location?.address?.zip;

    const updateConfiguration = useCallback((data) => {
        // override config with FF info
        data.isSingleColumn = enableNDSRightRail && !isXs;

        setConfig(data);
    }, [enableNDSRightRail, isXs]);

    // Send config request with data
    useEffect(() => {
        eventBus.emit(UDS_CONFIG_REQUEST, {
            buildConfig: true,
            listing,
            payments,
            interestRates,
        });

        // Listen for completed response to update local state
        eventBus.on(UDS_CONFIG_COMPLETED, updateConfiguration);

        return () => {
            eventBus.off(UDS_CONFIG_COMPLETED, updateConfiguration);
        };
    }, [payments, interestRates, listing, updateConfiguration]);

    // destructure listing and declare variables
    const {
        dealerConfig: { hideAds } = {},
        certifiedProduct = {},
        electricComponentInfo = {},
        id: listingId,
        hasSpecialOffer,
        listingType,
        owner = {},
        paymentServices = {},
        pricingDetail = {},
        type,
        vin,
    } = listing;

    const {
        isWestlakeEnabled = false,
    } = usePreQualEligibility(listing);

    const { dealerPreferredCTA, privateSeller: isPrivateSeller, sacaMmdId, id: ownerId } = owner;

    const showRatingsAndReviews = isConsumerRatingsEnabled && isConsumerRatingsVdpEnabled && hasVehicleReviews;

    const getAdditionalInventory = useLeadAdditionalInventory(true, ownerId);

    const { incentivesUrl, digitalRetailingType, dealerSettings = {} } = paymentServices;

    const { batteryDegradationInfo: { healthRating } = {} } = electricComponentInfo;

    // exclude battery health on FBA if health rating is below 80
    const showBatteryHealth = enableBatteryHealth && electricComponentInfo?.batteryDegradationInfo && ((fordBatteryHealth && healthRating >= healthRatingThreshold) || !fordBatteryHealth);

    // Ads
    let showMrec = false;
    let showMrecMobile = false;
    let showMma = false;
    if (isAdsEnabled && (!hideAds || isPrivateSeller)) {
        showMrecMobile = isMrecEnabled && isXs;
        showMrec = isMrecEnabled && (isAtcBranded && isGreaterThanSm) || (isKbbBranded && isLg);
        showMma = isMmaEnabled && (isAtcBranded && !isXs || isKbbBranded && isLg);
    }

    // pricing & payments
    const { salePrice = '', msrp = '' } = pricingDetail;
    const hasPrice = !!(salePrice || msrp);
    const price = salePrice || msrp;

    // Wallet
    const enableStandaloneCreditApp = isStandaloneCreditAppEnabled && !!sacaMmdId && !!creditAppHost;

    const walletNotifCache = new BrowserStorage('WALLET_NOTIF_POPOVER', { persist: true });
    const showWalletAlert = walletNotifCache.getItem()?.show && showWalletAlertNotification;

    useEffect(() => {
        sendImpressions({ name: 'dealerSegmentationImpression' });

        if (!isPrivateSeller && window.location.hash === '#showEmailModal') {
            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                showEmailModal: true,
            }));
        }

        // Reset the ref when visiting another VDP
        emailInputClickedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingId]);

    useEffect(() => {
        if (isPrivateSeller && zip && vin) {
            PsxPrequalEligibilityCheckFetcher({ zip })
                .then(({ userLocationIsFinanceEligible }) => {
                    if (userLocationIsFinanceEligible) {
                        dispatch(inventoryDuck.creators.updateInventoryItem(listingId, {
                            creditApp: {
                                url: `/marketplace/financing?vin=${vin}`,
                                partner: 'psx',
                                ctaLabel: 'Get Pre-Qualified',
                            },
                        }));
                    }
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zip, vin]);

    useEffect(() => {
        if (getCompareListingsIds().length > 0) {
            dispatch(compareListingsDuck.creators.loadListingsData(getCompareListingsIds(), 5));
        }
        dispatch(userPreferencesDuck.creators.setCompareToggle(getCompareListingsToggle()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getIncentiveOffers = useCallback(async () => {
        const incentivesParams = { dealType, zip };
        const incentives = await getIncentives({
            incentivesFetcher: paymentIncentivesFetcher,
            incentivesUrl,
            incentivesParams,
        });
        dispatch(paymentsDuck.creators.updateListingIncentives({
            [listingId]: {
                [dealType]: incentives[0]?.hasError ? incentives : transformIncentives(incentives, incentiveIds),
            },
        }));
    }, [dispatch, dealType, zip, incentivesUrl, listingId, incentiveIds, paymentIncentivesFetcher]);

    const hasIncentivesOffers = !!incentivesUrl && (pencilingPayments[listingId]?.expectAvailableOffers || listingPayments[listingId]?.expectAvailableOffers || incentiveIds?.length);

    const enabledAmdIncentivesOffers = !hasSpecialOffer && hasIncentivesOffers;

    const {
        creditType,
        defaultLeaseDownPayment,
        downPayment,
        downPaymentPercent,
    } = getAppliedDealerSettings({
        dealerSettings,
        walletInteractions: interactions,
        price,
        payments,
        nationalRates: interestRates,
    });

    const hasExistingDeal = enableDeals && !_isEmpty(memoizedExistingDeal);

    const handleOpenSDP = useCallback(() => dispatch(vdpActiveInteractionDuck.creators.setKeys({
        emailCTAContext: '',
        showEmailModal: false,
    })), [dispatch]);

    const handleErrorOpenSDP = useCallback(() => dispatch(vdpActiveInteractionDuck.creators.setKeys({
        emailCTAContext: 'calculatorOfferSummary',
        showEmailModal: true,
    })), [dispatch]);

    const handleEditSettingsClick = () => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showEmailModal: true,
            showEditEmailModal: true,
        }));
    };

    const paymentsInfo = useMemo(() => ({
        ...payments,
        creditType,
        downPayment: downPayment || calculateDownPayment(price, { downPayment, downPaymentPercent }),
        dueAtSigning: defaultLeaseDownPayment || calculateDownPayment(price, {
            downPayment: paymentEmphasis ? downPayment : defaultLeaseDownPayment,
            downPaymentPercent: paymentEmphasis ? downPaymentPercent : 0.10,
        }),
    }), [creditType, defaultLeaseDownPayment, downPayment, downPaymentPercent, paymentEmphasis, payments, price]);

    const renderDigitalRetailOverlayCTA = (
        <DigitalRetailOverlayCTA
            listedPrice={price}
            listing={listing}
            paymentsInfo={paymentsInfo}
            paymentsDuck={paymentsDuck}
            onClick={handleOpenSDP}
            onError={handleErrorOpenSDP}
            uiContext="incentives"
            userDuck={userDuck}
            isLoggedIn={isLoggedIn}
        />
    );

    const isAccelerate = digitalRetailingType === 'accelerate';

    const prequalCtaProps = useMemo(() => ({
        enableSDPCreditApp: enableSDPCreditApp && isAccelerate,
        enableSACAStickyHeader: enableStandaloneCreditApp && !isAccelerate,
        onError: handleErrorOpenSDP,
        listedPrice: price,
        userDuck,
        isLoggedIn,
        paymentsInfo,
    }), [enableSDPCreditApp, enableStandaloneCreditApp, handleErrorOpenSDP, isAccelerate, isLoggedIn, paymentsInfo, price]);

    const onEmailCTAClick = useCallback((context) => {
        if (context === 'incentives' && hasSpecialOffer) {
            dispatch(remoteActions.getAvailableOffers({ id: listingId }));
        }
        if (context === 'incentives' && enabledAmdIncentivesOffers) {
            getIncentiveOffers();
        }
        if (context === 'contactTheDealer') {
            sendClick(ownerEmailClick, null, {
                par: 'v_lstg',
                includePageId: true,
                inventoryId: listingId,
                ownerId,
                context: 'contactTheDealer',
            });
        }
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: context,
            showEmailModal: true,
        }));
        dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listingId]));
    }, [hasSpecialOffer, enabledAmdIncentivesOffers, dispatch, listingId, getIncentiveOffers, ownerId]);

    // Ref variable to store the vi360 zIndex before its modified
    const vi360ZIndex = useRef(null);

    // This use effect is needed to reposition the email modal zIndex in case the vi360 modal exists on the page.
    useEffect(() => {
        const vi360Modal = document.getElementsByClassName('ff_modal')?.[0];
        if (vi360Modal) {
            const vi360ModalComputedZIndex = window.getComputedStyle(vi360Modal)?.zIndex;

            const modalBackdropElement = document.getElementsByClassName('modal-backdrop')?.[0];
            const modalBackdropComputedStyle = modalBackdropElement ? window.getComputedStyle(modalBackdropElement) : null;
            const computedZIndex = modalBackdropComputedStyle ? parseInt(modalBackdropComputedStyle.zIndex, 10) : 10100;

            if (showEmailModal) {
                vi360ZIndex.current = vi360ModalComputedZIndex;
                vi360Modal.style.zIndex = computedZIndex - 1;

            } else if (vi360ZIndex.current) {
                vi360Modal.style.zIndex = vi360ZIndex.current;
            }
        }
    }, [showEmailModal]);

    const showNDSRightRail = enableNDSRightRail && isGreaterThanSm;
    // enable Westlake Prequal CTA in mobile state
    const enableMobileWestlakeCTA = isWestlakeEnabled && isXs;

    // certifiedProduct
    const { id: certifiedType } = certifiedProduct;

    // chat
    const isChatEventEnabled = isChatEnabled && chatProviders.includes(owner?.chat?.provider ?? '');

    // Incentives
    const isIncentivesOfferEnabled = listingType?.toLowerCase() === 'new' && (hasSpecialOffer || enabledAmdIncentivesOffers);

    const pixallFirstPartyId = useMemo(() => getFirstPartyVistorId(), []);
    const pixallThirdPartyId = useMemo(() => getPixallId() || 'nl', []);

    const fireTelmetricsTag = useCallback((phoneNumber) => {
        const data = {
            birf,
            listing: {
                ...listing,
                vdpURL: getAbsolutePathWithListingId(window?.location?.origin, listingId),
            },
            phoneNumber,
        };
        sendClick(telMetricsPhoneTextClick, {}, data);
    }, [birf, listing, listingId]);

    const persistDealData = useCallback(() => {
        if (isNDSCalcEnabled && enableDeals && Object.keys(deal).length) {
            PersistDealDataService(atcHost, {
                listingId,
                pixallId: pixallFirstPartyId || pixallThirdPartyId,
                dealData: deal,
            });
        }
    }, [isNDSCalcEnabled, enableDeals, deal, atcHost, listingId, pixallFirstPartyId, pixallThirdPartyId]);

    const phoneAndTextHandler = useCallback((phoneNumber) => {
        fireTelmetricsTag(phoneNumber);
        persistDealData();
    }, [fireTelmetricsTag, persistDealData]);

    const setEmailConfirmationModal = useCallback((state) => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showEmailConfirmationModal: state,
        }));
    }, [dispatch]);

    const handleEmailInputInteraction = useCallback((event, context = '') => {
        // Should fire once per VDP when user interact with email input form for the first time
        if (!emailInputClickedRef.current) {
            sendClick(emailFormEngagementClick, event, { inventoryId: listing.id, uiContext: context });
            emailInputClickedRef.current = true;
        }
    }, [listing.id]);

    const updateMyWallet = useCallback((data) => {
        dispatch(paymentsDuck.creators.updateMyWallet(data));
    }, [dispatch]);

    const [oneClickEmailSent, setOneClickEmailSent] = useState(false);

    const [oneClickAdditionalInventory, setOneClickAdditionalInventory] = useState(false);

    useEffect(() => {
        setOneClickEmailSent(false);
    }, [listing?.id]);

    const [dealToSubmit, setDealToSubmit] = useState({});
    useEffect(() => {
        const { deal: dealData } = getDeal(listing, payments, interestRates, false, cashDealPrice, disableTrade);
        setDealToSubmit(dealData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(payments), // Use deep object comparison not referential equality
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(pricingDetail), // Use deep object comparison not referential equality
    ]);

    // This closes all existing modals when sending an email using additionalInventory in case additionalInventory is opened through another modal
    const handleAdditionalInventoryModalClose = () => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showEmailModal: false,
            showEditEmailModal: false,
            showEmailConfirmationModal: false,
        }));

        if (!oneClickAdditionalInventory) {
            setOneClickAdditionalInventory(false);
        }
    };

    const handleSendOneClickEmail = useCallback(async (event) => {

        const isUserInvolved = paymentEmphasis || hasUpdatedWallet || hasUpdatedNDSCalc;
        const enableSaveDeal = hasPrice && enableDeals && isNDSCalcEnabled && isUserInvolved;

        sendClick(ownerEmailSent, event, {
            inventoryId: listingId,
            hasListingType: true,
            includeWalletData: false,
            par: 'v_lstg',
            context: 'contactTheDealer',
        });

        const { emailResult, responseDealXgId } = await sendEmailAndSaveDeal({
            cookies,
            deal: dealToSubmit,
            dealXgIdRef,
            dealsDuck,
            disableTrade,
            dispatch,
            emailOwnerFetcher: EmailOwnerFetcher,
            enableSaveDeal,
            env: publicRuntimeConfig.environment === 'prod' ? 'prod' : 'staging',
            formData: {
                comments: 'Please confirm vehicle availability.',
                firstName: leadProfile.firstName,
                fromEmailAddress: leadProfile?.email,
                lastName: leadProfile?.lastName,
                phoneNumber: leadProfile?.phone,
            },
            isAccelerate,
            listing,
            oneClick: true,
            originPage: 'vdp',
            owner,
            paymentsDuck,
            pixallFirstPartyId,
            pixallThirdPartyId,
            saveDealXgFetcher,
            userZip: zip,
            walletId: payments?.walletId,
        });

        dealXgIdRef.current = responseDealXgId;

        if (emailResult?.email?.status === 'SUCCESS') {
            setOneClickAdditionalInventory(true);
            setOneClickEmailSent(true);
            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                showEmailConfirmationModal: true,
            }));
        }

    }, [cookies, dispatch, leadProfile, listing, listingId, pixallFirstPartyId, pixallThirdPartyId, zip, dealToSubmit, enableDeals, hasPrice, hasUpdatedNDSCalc, hasUpdatedWallet, isAccelerate, isNDSCalcEnabled, paymentEmphasis, disableTrade, owner, payments?.walletId, publicRuntimeConfig.environment]);

    const handleVHROpenNewTab = useCallback(async () => {
        const vhrTiles = listing?.vhrTiles || [];

        if (vhrTiles.length === 0) {
            return;
        }

        const { link, tileType } = configureExperianTile(brand, vhrTiles[0]);
        const vhrLink = link?.href;

        if (!vhrLink) {
            return;
        }

        const isCarfaxTile = tileType?.split('_')[0] === 'CARFAX';

        try {
            await new Promise((resolve) => { setTimeout(resolve, 1000); });

            if (isCarfaxTile && removeCarfaxTile) {
                window.open(atob(vhrLink), '_blank');
            } else {
                window.open(vhrLink, '_blank');
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to open the link:', error);
        }
    }, [brand, listing?.vhrTiles, removeCarfaxTile]);

    const handleContactDealerCTA = useCallback((data) => {
        const { placement } = data;

        switch (placement) {
            case 'vi360':
                onEmailCTAClick('contactTheDealer');
                break;
            case 'lenderPaymentCTA':
                if (isOneClickVDPEnabled) {
                    handleSendOneClickEmail();
                } else {
                    onEmailCTAClick('contactTheDealer');
                }
                break;
            default:
                break;
        }
    }, [handleSendOneClickEmail, isOneClickVDPEnabled, onEmailCTAClick]);

    useEffect(() => {
        eventBus.on(WALLET_EMAIL_DEALER_REQUEST, handleContactDealerCTA);

        return () => {
            eventBus.off(WALLET_EMAIL_DEALER_REQUEST, handleContactDealerCTA);
        };
    }, [payments, interestRates, isOneClickVDPEnabled, listing, updateConfiguration, onEmailCTAClick, handleSendOneClickEmail, handleContactDealerCTA]);

    const [isBreakdownConfirmation, setIsBreakdownConfirmation] = useState(false);

    useEffect(() => {
        if (showEmailModal === false) {
            setIsBreakdownConfirmation(false);
        }
    }, [showEmailModal]);

    const enableShowOfferSingleClick = !isAccelerate && !!leadProfile?.email && !!leadProfile?.firstName && !!leadProfile?.lastName && !hasExistingDeal;

    const onSendOfferClick = useCallback(async (context) => {
        if (context === 'incentives' && hasSpecialOffer) {
            dispatch(remoteActions.getAvailableOffers({ id: listingId }));
        }
        if (context === 'incentives' && enabledAmdIncentivesOffers) {
            getIncentiveOffers();
        }

        const isUserInvolved = paymentEmphasis || hasUpdatedWallet || hasUpdatedNDSCalc;
        const enableSaveDeal = hasPrice && enableDeals && isNDSCalcEnabled && isUserInvolved;
        const saveIcoData = listing?.esntial && !listing?.esntial?.deemphasized && listing?.ecommWebsite && !!Object.keys(listing?.ecommWebsite).length && payments?.tradeIn?.ico?.length;

        const vehicleTitle = listing?.title || listing?.listingtitle || 'vehicle';
        const priceClause = price ? ` for ${formatCurrency(price)}` : '';
        const emailMessage = `I want to make an offer on your ${vehicleTitle} listed${priceClause}.`;

        const { emailData, emailResult, responseDealXgId } = await sendEmailAndSaveDeal({
            consumerBrand,
            icoTradeIn: (saveIcoData ? payments?.tradeIn?.ico?.[0] : undefined),
            cookies,
            copySelf,
            deal: dealToSubmit,
            dealXgIdRef,
            dealsDuck,
            disableTrade,
            dispatch,
            emailOwnerFetcher: EmailOwnerFetcher,
            enableSaveDeal,
            env: publicRuntimeConfig.environment === 'prod' ? 'prod' : 'staging',
            formData: {
                comments: emailMessage,
                firstName: leadProfile.firstName,
                fromEmailAddress: leadProfile?.email,
                lastName: leadProfile?.lastName,
                phoneNumber: leadProfile?.phone,
            },
            includeWalletData: true,
            isAccelerate,
            listing,
            originPage: 'vdp',
            owner,
            partnerName,
            paymentsDuck,
            pixallFirstPartyId,
            pixallThirdPartyId,
            saveDealXgFetcher,
            userZip: zip,
            walletId: payments?.walletId,
        });

        dealXgIdRef.current = responseDealXgId;

        if (emailResult?.email?.status === 'SUCCESS') {
            setIsBreakdownConfirmation(true);

            // Fires the email tag
            sendClick(ownerEmailSent, null, {
                ...emailData,
                deal: dealToSubmit,
                inventoryId: listingId,
                par: 'v_lstg',
                isChipEnabled: true,
                serviceInquiry: 'confirmAvailability',
                inquiryLabel: "This Vehicle's Availability",
                context: 'calculatorDealSubmitted',
                selectedVrsData: listing?.selectedVrsData,
                trimSelected: listing?.trimSelected,
            });

            dispatch(vdpActiveInteractionDuck.creators.setKeys({
                emailCTAContext: context,
                showEmailModal: true,
            }));

            dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listingId]));
        }

    }, [consumerBrand, payments?.tradeIn?.ico, dispatch, hasSpecialOffer, enabledAmdIncentivesOffers, listingId, getIncentiveOffers, cookies, leadProfile, listing, pixallFirstPartyId, pixallThirdPartyId, zip, dealToSubmit, enableDeals, hasPrice, hasUpdatedNDSCalc, hasUpdatedWallet, isAccelerate, isNDSCalcEnabled, paymentEmphasis, disableTrade, owner, payments?.walletId, publicRuntimeConfig.environment, price, copySelf, partnerName]);

    const emailOwnerProps = useMemo(() => ({
        activeEmailListingDuck: vdpActiveEmailListingDuck,
        activeInteractionDuck,
        alternateFormContent: (
            <DigitalRetailOverlayCTA
                listing={listing}
                paymentsDuck={paymentsDuck}
                uiContext="testDrive"
                userDuck={userDuck}
                isLoggedIn={isLoggedIn}
                dealsDuck={dealsDuck}
            />
        ),
        enableConsumerMetadata: true,
        currentPageNameDuck,
        saveDealXgFetcher,
        env: environment === 'prod' ? 'prod' : 'staging',
        handleAdditionalInventoryModalClose,
        handleEditSettingsClick,
        handleEmailInputInteraction,
        setOneClickEmailSent,
        fetcher: EmailOwnerFetcher,
        interestRateDuck,
        isLoggedIn,
        nextStepsProps: {
            getAdditionalInventory,
            nextSteps: <EmailFormNextStepsContainer />,
        },
        ownerId,
        oneClickAdditionalInventory,
        oneClickEmailSent,
        oneClickOptIn: enableOneClickVDP,
        paymentsDuck,
        dealsDuck,
        setEmailConfirmationModal,
        updateMyWallet,
        smsSubscribeFetcher: SmsSubscribeFetcher,
        showEmailConfirmationModal,
        userDuck,
        isBreakdownConfirmation,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [updateMyWallet, isLoggedIn, ownerId, showEmailConfirmationModal, showEmailModal, getAdditionalInventory, listing]);

    const emailCTAProps = useMemo(() => ({
        buttonStyle: dealerPreferredCTA === 'WEBSITE' ? 'secondary' : 'primary',
        listingId,
        onClick: onEmailCTAClick,
    }), [onEmailCTAClick, listingId, dealerPreferredCTA]);

    const oneClickCTAProps = useMemo(() => ({
        handleSendOneClickEmail,
        oneClickEmailSent,
    }), [handleSendOneClickEmail, oneClickEmailSent]);

    // setting LazyLoad preserve heights
    let purchaseConfidenceHeight;
    let ndsHeight;
    let pricingHeight;
    let psxComponentsHeight;
    let batteryHealthHeight;
    switch (device?.mediaType) {
        case 'lg':
            ndsHeight = showNDSRightRail ? 1013 : 895;
            pricingHeight = 884;
            batteryHealthHeight = 290;
            break;
        case 'sm':
            purchaseConfidenceHeight = 225;
            ndsHeight = 919;
            psxComponentsHeight = 515;
            batteryHealthHeight = 286;
            break;
        case 'xs':
            purchaseConfidenceHeight = 225;
            ndsHeight = 1267;
            psxComponentsHeight = 480;
            batteryHealthHeight = 410;
            break;
        case 'md':
        default:
            purchaseConfidenceHeight = 244;
            ndsHeight = showNDSRightRail ? 1082 : 954;
            pricingHeight = 872;
            psxComponentsHeight = 430;
            batteryHealthHeight = 375;
    }

    const renderPrimaryVdpCtaContainer = useMemo(() => (
        <PrimaryVdpCallsToActionContainer
            key="calls-to-action"
            emailCTAProps={emailCTAProps}
            oneClickCTAProps={oneClickCTAProps}
            phoneAndTextHandler={phoneAndTextHandler}
        />
    ), [emailCTAProps, oneClickCTAProps, phoneAndTextHandler]);

    /**
     * STICKY HEADER
     */
    const sectionStickyHeader = useMemo(() => {
        if (isPrivateSeller) {
            return (
                <>
                    <LazyComponent>
                        <StickyHeaderContainer />
                    </LazyComponent>
                    <LazyComponent>
                        <PsxBubbleChat />
                    </LazyComponent>
                </>
            );
        }
        return (
            <LazyComponent>
                <StickyHeaderContainer
                    emailCTAProps={emailCTAProps}
                    oneClickCTAProps={oneClickCTAProps}
                    phoneClickHandler={phoneAndTextHandler}
                    existingDeal={memoizedExistingDeal}
                    prequalCtaProps={prequalCtaProps}
                />
            </LazyComponent>
        );
    }, [isPrivateSeller, emailCTAProps, oneClickCTAProps, memoizedExistingDeal, phoneAndTextHandler, prequalCtaProps]);

    /**
     * WALLET NOTIFICATION
     */
    const sectionWalletNotification = useMemo(() => showWalletAlert && !isXs && (
        <WalletAlertsContainer enableAlert={showWalletAlert} />
    ), [showWalletAlert, isXs]);

    /**
     * PSX VERIFICATION
     */
    const sectionPsxVerification = useMemo(() => (isPrivateSeller && (
        <div
            style={{ minHeight: '45px' }}
            key="verification"
        >
            <LazyComponent key="verification">
                <PsxVerificationContainer key="verification" />
            </LazyComponent>
        </div>
    )), [isPrivateSeller]);

    /**
     * BANNER
     */
    const sectionBanner = useMemo(() => {
        if (isPrivateSeller) {
            return [
                <ListingErrorContainer key="psx_error" />,
                <VehicleDetailsTitleContainer
                    key="psx_title"
                    showRatingsAndReviews={showRatingsAndReviews}
                    onEmailCTAClick={onEmailCTAClick}
                />,
            ];
        }
        return [
            <ListingErrorContainer key="error" />,
            <VehicleDetailsTitleContainer
                key="title"
                onEmailCTAClick={onEmailCTAClick}
            />,
        ];
    }, [isPrivateSeller, showRatingsAndReviews, onEmailCTAClick]);

    /**
     * SIMLAR INVENTORY
     */
    const sectionSimilarInventory = (!isPrivateSeller && isSimilarInventoryEnabled && isVdpCarouselHigherPlacementEnabled) && (
        <LazyComponent
            key="owner_similar_inventory"
            fallback={similarIventoryFallback}
        >
            <SimilarInventoryContainer />
        </LazyComponent>
    );

    /**
     * MEDIA
     */
    const sectionMedia = useMemo(() => [
        <MediaGallery
            key="media"
            emailCTAProps={emailCTAProps}
            phoneClickHandler={phoneAndTextHandler}
        />,
    ], [emailCTAProps, phoneAndTextHandler]);

    /**
     * SIDEBAR
     */
    const sectionSidebar = useMemo(() => {
        if (isPrivateSeller) {
            return [
                ((
                    <PricingSnapshotContainer
                        key="pricing_prequal"
                        config={config}
                    />
                )),

                isXs && (
                    <ClientOnly
                        fallback={(
                            <LoadingSpinner
                                className="margin-top-4 margin-bottom-5"
                                height={40}
                            />
                        )}
                        key="psx_cta_container"
                    >
                        <PsxCtaContainer
                            className="margin-top-4 margin-bottom-5"
                            layout="makeOffer"
                            minHeight="40px"
                        />
                    </ClientOnly>
                ),
                isXs && <SpecificationsGridContainer key="psx_specs_mobile" />,
                showNDSRightRail && (
                    <LazyComponent
                        key="nds_right_rail"
                        fallback={(
                            <LoadingSpinner
                                // required for jumplink
                                id="nativeDealContainer"
                                height={ndsHeight}
                            />
                        )}
                    >
                        <Panel>
                            <MyWalletContainer
                                onEmailCTAClick={onEmailCTAClick}
                                enableCreditAppLink={enableStandaloneCreditApp}
                                isRightRail
                                config={config}
                            />
                        </Panel>
                    </LazyComponent>
                ),
                (
                    <AffixedSidebar
                        id={AFFIX_ID}
                        affixedGreaterThan="sm"
                        key="psx_sidebar"
                    >
                        <ClientOnly
                            key="psx_sidebar"
                            fallback={<LoadingSpinner height={psxComponentsHeight} />}
                        >
                            <PsxPrimerContainer inventoryId={listingId} />
                            <PsxLeadsContainer />
                            {showMrec && <ShowcaseAdSlotContainer />}
                        </ClientOnly>
                    </AffixedSidebar>
                ),
            ];
        }

        let sharedComponents = [
            isEsntialConfidentPurchaseEnabled && <PurchaseWithConfidenceSnapshotContainer key="pwc-snapshot" />,
            isXs && isEsntialConfidentPurchaseEnabled && renderPrimaryVdpCtaContainer,
            showNDSRightRail && (
                <LazyComponent
                    key="nds_right_rail"
                    fallback={(
                        <LoadingSpinner
                            // required for jumplink
                            id="nativeDealContainer"
                            height={ndsHeight}
                        />
                    )}
                >
                    <Panel
                        bsStyle=""
                    >
                        <MyWalletContainer
                            onEmailCTAClick={onEmailCTAClick}
                            enableCreditAppLink={enableStandaloneCreditApp}
                            isRightRail
                            config={config}
                        />
                    </Panel>
                </LazyComponent>
            ),
        ];
        sharedComponents = [
            <PricingSnapshotContainer
                key="pricing_prequal"
                emailCTAProps={isIncentivesOfferEnabled ? emailCTAProps : undefined}
                config={config}
            />,
            !enableMobileWestlakeCTA && (
                <LazyComponent
                    key="prequal_cta_container"
                    fallback={null}
                >
                    <PreQualCTAContainer
                        uiContext="simplified"
                        emailCTAProps={emailCTAProps}

                    />
                </LazyComponent>
            ),
            isXs && !isEsntialConfidentPurchaseEnabled && renderPrimaryVdpCtaContainer,
            isXs && <SpecificationsGridContainer key="specs_mobile" />,
            ...sharedComponents,
        ];
        if (!isXs) {
            sharedComponents = [
                ...sharedComponents,
                <PrimaryVdpCallsToActionContainer
                    emailCTAProps={emailCTAProps}
                    key="primary-vdp-cta"
                />,
            ];
        }
        return [
            ...sharedComponents,
            <AffixedSidebar
                id={AFFIX_ID}
                affixedGreaterThan="sm"
                key="sidebar"
            >
                <ClientOnly key="sidebar">

                    <DealerLeadsContainer
                        phoneAndTextHandler={phoneAndTextHandler}
                        emailCTAProps={emailCTAProps}
                        emailOwnerProps={emailOwnerProps}
                        persistDealData={persistDealData}
                    />
                </ClientOnly>
            </AffixedSidebar>,
        ];
    }, [isPrivateSeller, showNDSRightRail, ndsHeight, onEmailCTAClick, enableStandaloneCreditApp, config, isIncentivesOfferEnabled, emailCTAProps, isXs, phoneAndTextHandler, enableMobileWestlakeCTA, emailOwnerProps, persistDealData, psxComponentsHeight, listingId, showMrec, isEsntialConfidentPurchaseEnabled, renderPrimaryVdpCtaContainer]);

    /**
     * AD SLOTS
     */
    const renderAdSlots = useMemo(() => [
        (showMma) && (
            <LeaderBoardAdSlotContainer
                key="leaderboard-adslot-container"
                isInColumn
            />
        ),
        (showMrecMobile) && (
            <ShowcaseAdSlotContainer
                key="showcase-adslot-container"
                className={clsx(
                    'padding-vertical-4',
                    {
                        'margin-bottom-5': isPrivateSeller,
                        'padding-vertical-md-5': !isPrivateSeller,
                    }
                )}
            />
        ),
    ], [isPrivateSeller, showMma, showMrecMobile]);

    /**
     * UPPER DETAILS
     */
    const sectionUpperDetails = useMemo(() => {

        const handleEmailCTAClick = () => {
            if (isPrivateSeller || !enableShowOfferSingleClick) {
                return onEmailCTAClick;
            }
            return onSendOfferClick;
        };

        const renderMyWalletContainer = hasPrice && !showNDSRightRail && (
            <LazyComponent
                key={isPrivateSeller ? 'psx_nds' : 'nds'}
                fallback={(
                    <LoadingSpinner
                        // required for jumplink
                        id="nativeDealContainer"
                        height={ndsHeight}
                    />
                )}
            >
                <MyWalletContainer
                    onEmailCTAClick={handleEmailCTAClick()}
                    enableCreditAppLink={enableStandaloneCreditApp}
                    config={config}
                />
            </LazyComponent>
        );

        const renderLenderPreQualWrapper = (
            <LenderPreQualWrapper
                key="lenderPreQualWrapper"
                listing={listing}
                zip={zip}
            />
        );

        if (isPrivateSeller) {
            return [
                renderMyWalletContainer,
                renderLenderPreQualWrapper,
                <LazyComponent key="psx_vehicle_details"><AdditionalVehicleDetailsContainer /></LazyComponent>,
                <ClientOnly key="psx_lazy_features">
                    <VehicleFeaturesContainer />
                </ClientOnly>,
                showRatingsAndReviews && (
                    <LazyComponent
                        key="psx_ratings_reviews_lazy"
                        fallback={<Anchor id="rating-reviews-section" />}
                    >
                        <RatingsAndReviewsContainer
                            key="ratings_reviews"
                            isInColumn={isPrivateSeller}
                        />
                    </LazyComponent>
                ),
                <LazyComponent
                    key="psx_pwc_container"
                    fallback={(
                        <LoadingSpinner id="purchaseConfidence" />
                    )}
                >
                    <PurchaseWithConfidenceContainer />
                </LazyComponent>,
                ...renderAdSlots,
            ];
        }

        return [
            renderMyWalletContainer,
            renderLenderPreQualWrapper,
            <ClientOnly key="vehicle_details">
                <AdditionalVehicleDetailsContainer />
            </ClientOnly>,
            <ClientOnly key="lazy_features">
                <VehicleFeaturesContainer />
            </ClientOnly>,
            (showBatteryHealth) && (
                <LazyComponent
                    key="battery_health"
                    fallback={(
                        <LoadingSpinner
                            id="batteryHealthSection"
                            height={batteryHealthHeight}
                        />
                    )}
                >
                    <BatteryHealthContainer images={batteryHealthImages} />
                </LazyComponent>
            ),
            <LazyComponent
                fallback={(
                    <LoadingSpinner
                        height={pricingHeight}
                        id="pricingSection"
                    />
                )}
                key="pricing_details"
            >
                <PricingDetailsContainer
                    emailCTAProps={emailCTAProps}
                    disablePriceBreakdown={disablePriceBreakdown}
                />
            </LazyComponent>,
            (type === 'Certified' || thirdPartyCertTypes.includes(certifiedType)) && (
                <LazyComponent
                    key="certified"
                    fallback={<Anchor id="warrantySection" />}
                >
                    <CertifiedProgramOfferContainer />
                </LazyComponent>
            ),
            <LazyComponent
                key="pwc_container"
                fallback={(
                    <LoadingSpinner
                        height={purchaseConfidenceHeight}
                        id="purchaseConfidence"
                    />
                )}
            >
                <PurchaseWithConfidenceContainer
                    emailCTAProps={emailCTAProps}
                    onEmailCTAClick={onEmailCTAClick}
                />
            </LazyComponent>,
            ...renderAdSlots,
            (showRatingsAndReviews && (
                <LazyComponent
                    key="ratings_reviews_lazy"
                    fallback={<Anchor id="rating-reviews-section" />}
                >
                    <RatingsAndReviewsContainer isInColumn />
                </LazyComponent>
            )),
        ];
    }, [isPrivateSeller, hasPrice, showNDSRightRail, ndsHeight, enableShowOfferSingleClick, onSendOfferClick, onEmailCTAClick, enableStandaloneCreditApp, config, showBatteryHealth, batteryHealthHeight, batteryHealthImages, pricingHeight, emailCTAProps, disablePriceBreakdown, type, certifiedType, purchaseConfidenceHeight, renderAdSlots, showRatingsAndReviews, listing, zip]);

    /**
     * SUPPORT CONTAINERS
     */
    const sectionSupportContainers = useMemo(() => {
        if (isPrivateSeller) {
            return null;
        }
        return [
            <VehicleDetailsEmailModalContainer
                {...emailOwnerProps}
                {...(isIncentivesOfferEnabled && {
                    incentivesProps: {
                        additionalContent: (
                            <ModalScrollPanel>
                                <AvailableOffers
                                    offers={availableOffers || listingIncentives[listingId]?.[dealType]}
                                    wrappedInModal
                                />
                            </ModalScrollPanel>
                        ),
                        sideBarToModalTitle: 'Available Offers',
                        uiContext: 'split',
                        nextStepsProps: {
                            ...emailOwnerProps.nextStepsProps,
                        },
                        ...(enabledAmdIncentivesOffers && !hasExistingDeal && {
                            footerContent: renderDigitalRetailOverlayCTA,
                            buttonStyle: 'secondary',
                        }),
                    },
                })}
                ndsOfferBreakdownProps={{
                    additionalContent: (
                        <OfferSummary
                            config={config}
                            isBreakdownConfirmation={isBreakdownConfirmation}
                        />
                    ),
                    sideBarToModalTitle: 'Send Offer',
                }}
                vhrLeadGateProps={{
                    sideBarToModalTitle: 'Vehicle History Report',
                    uiContext: 'vehicleHistoryReport',
                    handleVHROpenNewTab,
                }}
                key="email_modal"
            />,
            isChatEventEnabled && (
                <OwnerChatEvents
                    listingId={listingId}
                    ownerId={ownerId}
                    key="chat_events"
                    chatCallBack={persistDealData}
                />
            ),
            enableStandaloneCreditApp && (
                <StandaloneCreditAppContainer
                    key="saca_modal"
                />
            ),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPrivateSeller, emailOwnerProps, listing, enableStandaloneCreditApp, isChatEventEnabled, persistDealData, availableOffers, isIncentivesOfferEnabled, listingIncentives, config, dealType, listingId, enabledAmdIncentivesOffers, hasExistingDeal]);

    /**
     * This return block should only contain the basic layout of the VDP
     * any new containers or components should be added
     * to the appropriate sections above.
     */
    return (
        <>
            {sectionStickyHeader}
            <StickyContainer>
                <VdpPaginationContainer key="pagination" />
                <div className={clsx(
                    'container',
                    {
                        'margin-top-5': !isPrivateSeller,
                        'margin-top-3': isPrivateSeller,
                    }
                )}
                >
                    {sectionWalletNotification}
                    <div className="row">
                        {/* Any components in these sections should NOT be
                          * wrapped in a PageSection or .container
                          */}
                        <div className="col-xs-12 col-sm-7 col-md-8">
                            {sectionPsxVerification}
                            {sectionMedia}
                            {sectionBanner}
                            {!isXs && <SpecificationsGridContainer key="specs" />}
                            {isGreaterThanSm && sectionSimilarInventory}
                            {isGreaterThanSm && sectionUpperDetails}
                        </div>
                        <div className="col-xs-12 col-sm-5 col-md-4 padding-left-md-3">
                            {sectionSidebar}
                        </div>
                        {!isGreaterThanSm && (
                            <div className="col-xs-12">
                                {sectionSimilarInventory}
                                {sectionUpperDetails}
                            </div>
                        )}
                    </div>
                </div>
                <Anchor id={AFFIX_ID} />
                <LowerDetails
                    onEmailCTAClick={onEmailCTAClick}
                    phoneAndTextHandler={phoneAndTextHandler}
                    chatClickHandler={persistDealData}
                />
            </StickyContainer>
            {sectionSupportContainers}
        </>
    );
}

export default memo(VehicleDetailsContainer, _isEqual);
